import type {
  GroupResponsibleState,
  ParticipantRoomAssignment,
} from '../../domain/types'

export interface RoomParticipantDiff {
  added: ParticipantRoomAssignment[]
  removed: ParticipantRoomAssignment[]
  unchanged: ParticipantRoomAssignment[]
}

export interface RoomParticipantSubtitleItem {
  id: string
  name: string
  status: 'unchanged' | 'added' | 'removed'
}

function findParticipantInfoByCustomerId(
  state: GroupResponsibleState,
  bookingCustomerId: number,
): ParticipantRoomAssignment {
  const participant = Object.values(state.participants).find(
    p => p.bookingCustomerId === bookingCustomerId,
  )

  return {
    id: participant?.id ?? String(bookingCustomerId),
    name: participant?.name ?? '',
    bookingCustomerId,
    isAssigned: true,
  }
}

export function isRoomChanged(state: GroupResponsibleState, roomId: string): boolean {
  const room = state.rooms[roomId]
  const stagedIds = state.stagedRoomAssignments?.[roomId]

  if (!room || !stagedIds) {
    return false
  }

  const originalIds = room.participantsInfo.map(p => p.bookingCustomerId)

  if (originalIds.length !== stagedIds.length) {
    return true
  }

  return stagedIds.some(id => !originalIds.includes(id))
}

export function getChangedRoomIds(state: GroupResponsibleState): string[] {
  if (!state.stagedRoomAssignments) {
    return []
  }

  return Object.keys(state.rooms).filter(roomId => isRoomChanged(state, roomId))
}

export function getChangedRoomsCount(state: GroupResponsibleState): number {
  return getChangedRoomIds(state).length
}

export function hasStagedRoomChanges(state: GroupResponsibleState): boolean {
  return getChangedRoomsCount(state) > 0
}

export function getUnassignedStagedParticipantIds(state: GroupResponsibleState): number[] {
  if (!state.stagedRoomAssignments) {
    return []
  }

  const originallyAssignedIds = Object.values(state.rooms)
    .flatMap(room => room.participantsInfo.map(p => p.bookingCustomerId))
  const stagedIds = new Set(Object.values(state.stagedRoomAssignments).flat())

  return originallyAssignedIds.filter(id => !stagedIds.has(id))
}

export function hasUnassignedStagedParticipants(state: GroupResponsibleState): boolean {
  return getUnassignedStagedParticipantIds(state).length > 0
}

export function getRoomParticipantDiff(
  state: GroupResponsibleState,
  roomId: string,
): RoomParticipantDiff {
  const room = state.rooms[roomId]
  const stagedIds = state.stagedRoomAssignments?.[roomId]

  if (!room || !stagedIds || !isRoomChanged(state, roomId)) {
    return {
      added: [],
      removed: [],
      unchanged: room?.participantsInfo ?? [],
    }
  }

  const originalIds = new Set(room.participantsInfo.map(p => p.bookingCustomerId))
  const stagedIdSet = new Set(stagedIds)

  const unchanged = room.participantsInfo.filter(p => stagedIdSet.has(p.bookingCustomerId))
  const removed = room.participantsInfo.filter(p => !stagedIdSet.has(p.bookingCustomerId))
  const added = stagedIds
    .filter(id => !originalIds.has(id))
    .map(id => findParticipantInfoByCustomerId(state, id))

  return { added, removed, unchanged }
}

export function getRoomParticipantSubtitleItems(
  state: GroupResponsibleState,
  roomId: string,
): RoomParticipantSubtitleItem[] {
  const diff = getRoomParticipantDiff(state, roomId)

  return [
    ...diff.unchanged.map(p => ({ id: p.id, name: p.name, status: 'unchanged' as const })),
    ...diff.added.map(p => ({ id: p.id, name: p.name, status: 'added' as const })),
    ...diff.removed.map(p => ({ id: p.id, name: p.name, status: 'removed' as const })),
  ].filter(item => Boolean(item.name))
}

export function getStagedRoomOccupancyText(state: GroupResponsibleState, roomId: string): string {
  const room = state.rooms[roomId]
  if (!room) {
    return ''
  }

  const stagedIds = state.stagedRoomAssignments?.[roomId]
  if (!stagedIds) {
    return room.occupancyText
  }

  return `${stagedIds.length}/${room.occupancy.max}`
}
